import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";

// Components
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import EmailDistributionLayout from "./EmailDistributionLayout";
import Title from "../../../common/Title";

// MUI
import { Grid } from "@mui/material";

import Button from "@/Layouts/components/Buttons/Button";
import { ICITEmailDistribution, ICITScheduledAccountNumbers } from "@/Settings/types/Common/model";
import { errorLogger } from "@/Common/helpers/logger";
import { IEmailErrors, IInputEmailDistribution, TEmailDistributionUpdateParams } from "@/Settings/types/SystemSettings/emailDistribution";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

import formatEmailDistribution from "@/Settings/helpers/SystemSettings/emailDistribution/formatEmailDistribution";
import { validateCitEmailDistribution } from "@/Settings/helpers/SystemSettings/emailDistribution/validateEmailDistribution";

type TEmailDistributionProps = {
	locationId: string;
	citEmailDistribution?: ICITEmailDistribution[];
	citScheduledAccountNumbers?: ICITScheduledAccountNumbers[];
};

const EmailDistribution = (props: TEmailDistributionProps) => {
	const { locationId, citEmailDistribution, citScheduledAccountNumbers } = props;
	const [emailDistribution, setEmailDistribution] = useState<IInputEmailDistribution[]>([]);
	const [errors, setErrors] = useState<IEmailErrors[]>([]);

	const { mutate: updateEmailDistributionData, isPending } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: async (input: IInputEmailDistribution[]) => {
			return updateSystemSettings(locationId, { citEmailDistribution: input }, "CIT_EMAIL_DISTRIBUTION");
		},
		onError: (error) => errorLogger(error),
	});

	const handleAddEmailDistribution = () => {
		const updated = [...emailDistribution];
		updated.push({
			toIndividuals: [],
			toNonEmployee: [],
			accountNumbers: [
				{
					sourceAccount: [],
					destinationAccount: "",
					sourceLabelId: "",
				},
			],
		});
		const validation = validateCitEmailDistribution(updated);
		setErrors(validation.errors);
		setEmailDistribution(updated);
	};

	const handleRemove = (index: number) => {
		const updated = [...emailDistribution];
		updated.splice(index, 1);
		setEmailDistribution(updated);
		update(updated);
	};

	const handleEmailDistribution = ({ index, key, value }: TEmailDistributionUpdateParams) => {
		const updated = [...emailDistribution];
		const updatedDistribution = { ...updated[index], [key]: value };
		updated[index] = updatedDistribution;
		setEmailDistribution(updated);
		update(updated);
	};

	const update = (updatedEmailDistribution: IInputEmailDistribution[] = []) => {
		const { errors: updateErrors, isValid } = validateCitEmailDistribution(updatedEmailDistribution);
		setErrors(updateErrors);
		if (isValid) {
			updateEmailDistributionData(updatedEmailDistribution);
		}
	};

	useEffect(() => {
		setEmailDistribution(formatEmailDistribution<ICITEmailDistribution>(citEmailDistribution ?? []));
		return () => {
			setEmailDistribution([]);
		};
	}, [citEmailDistribution]);

	return (
		<Grid container>
			<Grid item xs={12}>
				<Title title="Write-off Configuration(s) / Email Distribution(s)" className="!mb-[24px]" />
			</Grid>
			<Grid item xs={12}>
				{emailDistribution.map((distribution, key) => {
					const keyIndex = `cit-Email-Distribution-${key}`;
					return (
						<EmailDistributionLayout
							locationId={locationId}
							key={keyIndex}
							index={key}
							distribution={distribution}
							citScheduledAccountNumbers={citScheduledAccountNumbers}
							errors={errors?.[key]}
							onRemove={handleRemove}
							onActionUpdate={handleEmailDistribution}
							isPending={isPending}
						/>
					);
				})}
			</Grid>
			<Button
				variant="contained"
				text="Add Email Distribution"
				classes="btn-regular-add btn-small-height btn-regular-text !mt-[24px]"
				minWidth={"180px"}
				endIcon={<AddCircleRounded />}
				onClick={handleAddEmailDistribution}
			/>
		</Grid>
	);
};

export default EmailDistribution;
